'use client';

import React, { useState } from 'react';
import { useRegionPricing } from '@/features/payment/hooks/useRegionPricing';

const getfaqs = (priceText: string) => [
    {
        question: 'How long does it take to create a page?',
        answer: 'About 5 minutes. Answer a few questions about your idea, customize if you want, and publish. Most founders have their page live before their coffee gets cold.',
    },
    {
        question: 'Do I need design or coding skills?',
        answer: 'Not at all. Our AI generates the copy, layout, and design. You just answer questions about your product and tweak anything you want. If you can fill out a form, you can use Waitlistly.',
    },
    {
        question: 'Can I use my own domain?',
        answer: 'Yes! Use our free subdomain (yourproject.waitlistly.live) or connect your own custom domain. Both options are included with no extra fees.',
    },
    {
        question: 'How does pricing work?',
        answer: `Free to build and preview unlimited pages. When you are ready to go live, it is a one-time payment of ${priceText} per published site. No subscriptions, no hidden fees, no surprises.`,
    },
    {
        question: 'Can I export my email signups?',
        answer: 'Absolutely. Export your entire email list as CSV anytime with one click. Your data belongs to you—no restrictions, no extra charges, no lock-in.',
    },
    {
        question: 'What happens after I publish?',
        answer: 'Your page stays live indefinitely. Track views, signups, and conversion rates in your dashboard. Use the data to decide whether to build your product or pivot to a new idea.',
    },
];

export default function FAQSection() {
    const { priceText } = useRegionPricing();
    const faqs = getfaqs(priceText);
    return (
        <section className="py-16 sm:py-24 bg-gray-50">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-8 text-center">
                    Frequently Asked Questions
                </h2>

                <div className="space-y-3">
                    {faqs.map((faq) => (
                        <FAQItem key={faq.question} {...faq} />
                    ))}
                </div>
            </div>
        </section>
    );
}

interface FAQItemProps {
    question: string;
    answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div
            className={`rounded-2xl border overflow-hidden transition-all duration-300 ${isOpen ? 'border-violet-200 bg-violet-50 shadow-lg shadow-violet-100/50' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center gap-4 p-4 sm:p-5 text-left"
            >
                <span className="flex-1 text-base sm:text-lg font-semibold text-gray-900 pr-4">{question}</span>
                {/* Plus/Minus Toggle */}
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isOpen ? 'bg-violet-600' : 'bg-gray-100'}`}>
                    {isOpen ? (
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                    )}
                </div>
            </button>
            <div
                className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-48' : 'max-h-0'}`}
            >
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-gray-600 leading-relaxed text-sm sm:text-base">
                    {answer}
                </div>
            </div>
        </div>
    );
}
